import { formatCurrency, formatDate } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import {
    Banknote,
    Building2,
    CalendarRange,
    Handshake,
    KeyRound,
    RefreshCw,
    Server,
    UserCheck,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Chip, Typogaphy } from 'vnetwork-ui'
import {
    CONTRACT_DIRECTION_OPTIONS,
    CONTRACT_ROLE_OPTIONS,
    CONTRACT_STATUS,
} from '../../constants'
import ContractButtons from '../../shared/Buttons'
import { CustomerContract } from '../../types'

interface ContractInfoCardProps {
    contract: CustomerContract
    contractID: string | undefined
}

const ContractInfoCard = ({ contract, contractID }: ContractInfoCardProps) => {
    const { t } = useTranslation('contract')

    const status = CONTRACT_STATUS(t).find(
        (s) => s.value === contract.contract_status
    )
    const role = CONTRACT_ROLE_OPTIONS(t).find(
        (s) => s.value === contract.contract_role
    )
    const direction = CONTRACT_DIRECTION_OPTIONS(t).find(
        (s) => s.value === contract.contract_direction
    )
    const services = contract.services || []
    const primaryService = services[0]

    return (
        <div className="space-y-5">
            {/* Header row */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <Typogaphy className="font-medium text-primary">
                        {contract.contract_number_id}
                    </Typogaphy>
                    {status && (
                        <Chip
                            label={status.label}
                            size="small"
                            variant="contained"
                            color={status.color as any}
                        />
                    )}
                    {role && (
                        <Chip
                            label={role.label}
                            size="small"
                            variant="contained"
                            color={role.color as any}
                        />
                    )}
                    {direction && (
                        <Chip
                            label={direction.label}
                            size="small"
                            variant="outlined"
                            color={direction.color as any}
                        />
                    )}
                    {contract.sla_enable && (
                        <Chip
                            label="SLA"
                            size="small"
                            variant="outlined"
                            color="primary"
                        />
                    )}
                    {contract.nda_enable && (
                        <Chip
                            label="NDA"
                            size="small"
                            variant="outlined"
                            color="success"
                        />
                    )}
                </div>
                <div className="flex items-center gap-1">
                    <ContractButtons.Scanner />
                    <ContractButtons.Edit
                        contract={contract}
                        contractID={contractID}
                    />
                    <ContractButtons.Clone />
                    <ContractButtons.Renew
                        contract={contract}
                        contractID={contractID}
                    />
                    <ContractButtons.Terminate
                        contract={contract}
                        contractID={contractID}
                    />
                </div>
            </div>

            {/* Info grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoCell
                    icon={<Building2 className="w-4 h-4" />}
                    label={t('contract_owner')}
                    value={
                        <Link
                            to={generatePathDatacentral(paths.customerDetail, {
                                id: contract.customer_id,
                            })}
                            className="text-primary hover:underline text-sm"
                        >
                            {contract.customer_id} -{' '}
                            {contract.company_name || contract.customer_name}
                        </Link>
                    }
                />

                <InfoCell
                    icon={<UserCheck className="w-4 h-4" />}
                    label={t('contract_employee')}
                    value={contract.employee_name}
                />

                {contract.partner_id && (
                    <InfoCell
                        icon={<Handshake className="w-4 h-4" />}
                        label={t('partner_name')}
                        value={
                            <Link
                                to={generatePathDatacentral(
                                    paths.supplierDetail,
                                    {
                                        id: contract.partner_id,
                                    }
                                )}
                                className="text-primary hover:underline text-sm"
                            >
                                {contract.partner_name || contract.partner_id}
                            </Link>
                        }
                    />
                )}

                <InfoCell
                    icon={<CalendarRange className="w-4 h-4" />}
                    label={t('contract_effective_date')}
                    value={
                        contract.contract_effective_date
                            ? formatDate(contract.contract_effective_date)
                            : undefined
                    }
                    suffix={
                        contract.contract_expiry_date ? (
                            <span className="text-xs text-muted-foreground ml-1">
                                {' - '}
                                {formatDate(contract.contract_expiry_date)}
                            </span>
                        ) : undefined
                    }
                />

                <InfoCell
                    icon={<Banknote className="w-4 h-4" />}
                    label={t('contract_value')}
                    value={
                        contract.contract_value
                            ? formatCurrency(contract.contract_value)
                            : undefined
                    }
                />

                {contract.contract_key && (
                    <InfoCell
                        icon={<KeyRound className="w-4 h-4" />}
                        label={t('contract_key')}
                        value={
                            <span className="text-sm">
                                {contract.contract_key}
                            </span>
                        }
                    />
                )}

                {services.length === 1 && primaryService && (
                    <InfoCell
                        icon={<Server className="w-4 h-4" />}
                        label={t('contract_service')}
                        value={
                            <Link
                                to={generatePathDatacentral(
                                    paths.serviceDetail,
                                    { id: primaryService.service_id }
                                )}
                                className="text-primary hover:underline text-sm"
                            >
                                {primaryService.service_name}
                            </Link>
                        }
                    />
                )}

                {services.length > 1 && (
                    <InfoCell
                        icon={<Server className="w-4 h-4" />}
                        label={t('contract_services')}
                        value={`${services.length} ${t('services_count')}`}
                    />
                )}

                {contract.auto_renewable && (
                    <InfoCell
                        icon={<RefreshCw className="w-4 h-4" />}
                        label={t('auto_renewable')}
                        value={
                            <Chip
                                label={t('yes')}
                                size="small"
                                variant="contained"
                                color="success"
                            />
                        }
                    />
                )}
            </div>

            {/* Note section */}
            {contract.contract_note && (
                <div className="bg-muted/40 rounded-lg px-4 py-3">
                    <Typogaphy className="text-xs text-muted-foreground mb-1">
                        {t('contract_note')}
                    </Typogaphy>
                    <Typogaphy className="text-sm text-foreground/80 leading-relaxed">
                        {contract.contract_note}
                    </Typogaphy>
                </div>
            )}
        </div>
    )
}

/** Single info cell with icon, label on top, value below */
const InfoCell = ({
    icon,
    label,
    value,
    suffix,
}: {
    icon: React.ReactNode
    label: string
    value?: React.ReactNode
    suffix?: React.ReactNode
}) => (
    <div className="flex items-start gap-3 rounded-md bg-gray-50/10 px-3.5 py-2.5">
        <div className="mt-0.5 text-muted-foreground/60">{icon}</div>
        <div className="min-w-0 flex-1">
            <Typogaphy className="text-xs text-muted-foreground uppercase mb-0.5">
                {label}
            </Typogaphy>
            <div className="text-sm text-foreground truncate">
                {value || <span className="text-muted-foreground/40">---</span>}
                {suffix}
            </div>
        </div>
    </div>
)

export default ContractInfoCard
